/**
 * useRevealSession — the one owner of a match-trail session.
 *
 * A "session" is everything between pressing the CTA and the map going quiet
 * again: framing the camera, tracing the trails, docking the card into the
 * deck, browsing the queue and opening the reveal.
 *
 * Two things make this safe to run next to discovery polling and React's Strict
 * Mode double-mount, and they are the reason this is a hook rather than a pile
 * of `setTimeout`s in MapPage:
 *
 *   1. **A frozen queue.** `start()` snapshots the ranked matches. Polling keeps
 *      writing `matchedUserIds`, but the live session reads only its snapshot,
 *      so a refresh mid-entrance can't reorder the deck under the user's finger
 *      or re-point the trail they are watching.
 *   2. **A session id on every timer.** Each scheduled callback captures the id
 *      that was current when it was scheduled and no-ops if it has moved on.
 *      Cancelling, restarting, or a double-mount all bump the id, which retires
 *      every timer already in flight without needing to track them individually.
 *
 * Reduced motion is not a slower version of this — it is a different path that
 * resolves synchronously with no timers at all.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { MOTION } from "../styles/motionTokens";
import { prefersReducedMotion } from "../styles/particleField";
import { idStr } from "./tradeOpportunity";
import { animatedTargetIds, countMode } from "./matchRanking";

/** 160 ms after the primary beacon lands, its card starts flying to the deck. */
const DOCK_DELAY_MS = 160;

export const INITIAL_SESSION = {
  phase: "idle",
  sessionId: 0,
  queue: [],
  activeIndex: 0,
  /** Ids whose trail has finished drawing. */
  arrived: [],
  /** Ids whose reveal has been opened and closed at least once. */
  explored: [],
  /** 'none' | 'overview' | 'focus' — what the camera is framing. */
  frame: "none",
  /** True once the card has left the map for the deck slot. */
  docked: false,
  deckOpen: false,
  reduced: false,
  live: "",
};

/**
 * Actions that ESTABLISH a session id rather than belong to one. They always
 * carry an id the state has not seen yet, so they are exempt from the staleness
 * check below — checking them would reject every session before it began.
 */
const ESTABLISHES_SESSION = new Set(["start", "cancel"]);

/**
 * Every other action carries the session id it belongs to. The reducer drops
 * anything from a retired session, so a timer that fires one tick after
 * `cancel()` — the classic source of "the entrance replayed itself" — changes
 * nothing.
 */
const isStale = (state, action) =>
  !ESTABLISHES_SESSION.has(action.type) &&
  action.sessionId != null &&
  action.sessionId !== state.sessionId;

export function revealSessionReducer(state, action) {
  if (isStale(state, action)) return state;

  switch (action.type) {
    case "start": {
      const { queue, activeIndex, reduced, sessionId, live } = action;
      return {
        ...INITIAL_SESSION,
        sessionId,
        queue,
        activeIndex,
        reduced,
        phase: "framing",
        frame: countMode(queue.length) === "one" ? "focus" : "overview",
        live,
      };
    }

    case "phase":
      return { ...state, phase: action.phase };

    case "arrive": {
      const id = idStr(action.id);
      if (state.arrived.includes(id)) return state;
      return {
        ...state,
        arrived: [...state.arrived, id],
        // The first arrival is what ends "tracing"; later ones land while the
        // deck is already up and must not drag the phase backwards.
        phase: action.primary && state.phase === "tracing" ? "landing" : state.phase,
      };
    }

    case "dock":
      return { ...state, phase: "docking", docked: true, deckOpen: true };

    case "docked":
      return {
        ...state,
        docked: true,
        deckOpen: true,
        phase: state.phase === "docking" ? "browsing" : state.phase,
        // `live` may be a function of state, because the entrance schedules this
        // action before the user can act and a string baked in at schedule time
        // describes a selection that may since have moved. Same shape as
        // `select`, which has always resolved against state for this reason.
        live:
          typeof action.live === "function"
            ? action.live(state)
            : action.live ?? state.live,
      };

    case "select": {
      if (state.queue.length === 0) return state;
      const next = action.resolve(state);
      return { ...state, activeIndex: next, live: action.live(next, state) };
    }

    case "frame":
      return { ...state, frame: action.frame };

    case "openReveal":
      return { ...state, phase: "openingReveal" };

    case "closeReveal": {
      const current = state.queue[state.activeIndex];
      const id = current ? idStr(current.id) : null;
      const multi = state.queue.length > 1;
      return {
        ...state,
        explored:
          id && !state.explored.includes(id)
            ? [...state.explored, id]
            : state.explored,
        // A single match has nowhere to go back to, so the session ends with the
        // reveal. Two or more return to the deck at the same queue position.
        phase: multi ? "browsing" : "idle",
        frame: multi ? state.frame : "none",
        deckOpen: multi,
        docked: multi ? state.docked : false,
        live: "Match explored",
      };
    }

    case "cancel":
      // Keeps the bumped id so in-flight timers from the cancelled session are
      // already stale by the time they fire.
      return { ...INITIAL_SESSION, sessionId: action.sessionId };

    default:
      return state;
  }
}

/**
 * @param {object} opts
 * @param {(trader, meta) => void} opts.onOpenReveal fired when the reveal should mount
 * @param {(payload) => void} opts.onTraceStarted   analytics, aggregate only
 * @param {(payload) => void} opts.onDeckOpened     analytics, aggregate only
 * @param {(payload) => void} opts.onTargetSelected analytics, aggregate only
 * @param {number} opts.maxAnimated                 trail cap (default 3)
 */
export default function useRevealSession({
  onOpenReveal,
  onTraceStarted,
  onDeckOpened,
  onTargetSelected,
  maxAnimated,
} = {}) {
  const [state, dispatch] = useReducer(revealSessionReducer, INITIAL_SESSION);

  const timersRef = useRef([]);
  const sessionIdRef = useRef(0);
  // Synchronous mirror of "a session is live", because the derived `isLive`
  // lags a commit and cannot arbitrate two presses inside one React batch.
  const liveRef = useRef(false);
  /** Id signature of the live session's queue — see `start`. */
  const liveKeyRef = useRef(null);
  // Callbacks live in a ref so scheduling never depends on their identity — a
  // parent re-render must not be able to restart or retire a running entrance.
  const cbRef = useRef({});
  cbRef.current = { onOpenReveal, onTraceStarted, onDeckOpened, onTargetSelected };

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  /** Schedule work bound to the session that was live when it was scheduled. */
  const after = useCallback((sessionId, ms, fn) => {
    const handle = window.setTimeout(() => {
      if (sessionIdRef.current !== sessionId) return;
      fn();
    }, ms);
    timersRef.current.push(handle);
  }, []);

  const cancel = useCallback(() => {
    // Synchronously, so a cancel-then-start in one batch is allowed through.
    liveRef.current = false;
    clearTimers();
    sessionIdRef.current += 1;
    dispatch({ type: "cancel", sessionId: sessionIdRef.current });
  }, [clearTimers]);

  // ...and re-derive it from the committed phase, so the ref cannot drift out
  // of step with the sessions that end on their own — a lone match whose
  // reveal is dismissed retires the session without anyone calling `cancel`.
  useEffect(() => {
    liveRef.current = state.phase !== "idle";
    if (state.phase === "idle") liveKeyRef.current = null;
  }, [state.phase]);

  /**
   * Open a session over an already-ranked queue.
   * `focusId` points the selection at a specific trader (a pin's own CTA)
   * without changing the queue order.
   */
  const start = useCallback(
    (queue, { focusId = null, instant = false } = {}) => {
      if (!Array.isArray(queue) || queue.length === 0) return;
      // One press, one session — but only for the SAME queue.
      //
      // Callers cannot enforce this themselves: `isLive` is derived from state,
      // so inside a single React batch it still reads false on the second press,
      // and a guard built on it let the trace restart from the top and counted a
      // second `match_trace_started` for a session the user opened once.
      //
      // Keyed on the queue rather than on liveness alone, because restarting a
      // live session over DIFFERENT matches is a real and tested behaviour (a
      // refreshed set of results, a different pin's CTA): that must still retire
      // the first session's timers and begin again. What must not happen is the
      // same set of matches opening the same session twice.
      const queueKey = queue.map((t) => idStr(t.id)).join(",");
      if (liveRef.current && liveKeyRef.current === queueKey) return;
      liveRef.current = true;
      liveKeyRef.current = queueKey;

      clearTimers();
      sessionIdRef.current += 1;
      const sessionId = sessionIdRef.current;

      // `instant`: the map is unavailable, so the trails have nothing to draw
      // on and waiting for them only made the tap look dead (B-36).
      const reduced = instant || prefersReducedMotion();
      const mode = countMode(queue.length);
      const focusIndex = focusId
        ? queue.findIndex((t) => idStr(t.id) === idStr(focusId))
        : 0;
      const activeIndex = focusIndex >= 0 ? focusIndex : 0;
      const animatedIds = animatedTargetIds(queue, maxAnimated);

      dispatch({
        type: "start",
        sessionId,
        queue,
        activeIndex,
        reduced,
        live:
          queue.length === 1
            ? "1 trade match found"
            : `${queue.length} trade matches found`,
      });

      // `count`, not `matchCount`. These payloads go straight to `trackEvent`,
      // and `server/analytics/analyticsPrivacy.js` allowlists event properties:
      // anything not on the list is dropped in silence. `matchCount` is not on
      // it, so the number never arrived — the events counted sessions and
      // nothing else, while every client-side test that asserted the payload
      // passed. `count` is the allowlisted key, and it already means exactly
      // this ("how many matches") for `match_found`.
      // Covered by map/mapAnalyticsAllowlist.test.jsx, which runs these payloads
      // through the real server redactor.
      // `mode` had the same problem and is now allowlisted too, alongside the
      // `rank` that `onTargetSelected` sends — that one was the whole payload,
      // so `match_target_selected` was arriving as `{}`. Both are aggregates
      // (a count bucket and a 1-based deck position); neither narrows to a
      // person or a place.
      cbRef.current.onTraceStarted?.({ count: queue.length, mode });

      // Reduced motion: everything has already arrived, and we continue without
      // waiting. No stagger, no spark, and above all no artificial delay — the
      // wait exists to let an animation play, so with no animation there is
      // nothing to wait for.
      if (reduced) {
        animatedIds.forEach((id) =>
          dispatch({ type: "arrive", sessionId, id, primary: false })
        );
        if (mode === "one") {
          dispatch({ type: "openReveal", sessionId });
          cbRef.current.onOpenReveal?.(queue[activeIndex], { rank: activeIndex + 1 });
        } else {
          // Still passes through `dock` so the deck's own state is identical to
          // the animated path — only the flight between them is skipped.
          dispatch({ type: "dock", sessionId });
          dispatch({
            type: "docked",
            sessionId,
            live: (s2) => `Match ${s2.activeIndex + 1} of ${s2.queue.length} selected`,
          });
          cbRef.current.onDeckOpened?.({ count: queue.length });
        }
        return;
      }

      after(sessionId, MOTION.matchFrameMs, () => {
        dispatch({ type: "phase", sessionId, phase: "tracing" });

        animatedIds.forEach((id, i) => {
          const landsAt = MOTION.matchTrailStaggerMs * i + MOTION.matchLineDrawMs;
          after(sessionId, landsAt, () => {
            dispatch({ type: "arrive", sessionId, id, primary: i === 0 });

            // The deck rises off the PRIMARY landing, while the remaining trails
            // are still arriving — that overlap is what stops the entrance from
            // feeling like two separate animations played back to back.
            if (i === 0 && mode !== "one") {
              after(sessionId, DOCK_DELAY_MS, () => {
                dispatch({ type: "dock", sessionId });
                cbRef.current.onDeckOpened?.({ count: queue.length });

                // The phase machine leaves `docking` on its own schedule rather
                // than waiting to be told by the flight component. `completeDock`
                // still exists so the real flight can settle it exactly on its
                // last frame — but if that component never mounts (projection
                // failure, reduced motion, an unmount mid-flight) the session
                // must not be stranded in `docking` with a deck it can't use.
                after(sessionId, MOTION.matchDockFlightMs, () => {
                  dispatch({
                    type: "docked",
                    sessionId,
                    live: (s2) => `Match ${s2.activeIndex + 1} of ${s2.queue.length} selected`,
                  });
                });
              });
            }
          });
        });

        if (mode === "one") {
          const lastLanding =
            MOTION.matchTrailStaggerMs * Math.max(0, animatedIds.length - 1) +
            MOTION.matchLineDrawMs +
            MOTION.matchArrivalFlareMs;
          after(sessionId, lastLanding + 200, () => {
            dispatch({ type: "openReveal", sessionId });
            cbRef.current.onOpenReveal?.(queue[activeIndex], { rank: activeIndex + 1 });
          });
        }
      });
    },
    [after, clearTimers, maxAnimated]
  );

  /** Called by the dock flight when the card has landed in its slot. */
  const completeDock = useCallback(() => {
    const sessionId = sessionIdRef.current;
    dispatch({
      type: "docked",
      sessionId,
      live: "Match 1 of " + (state.queue.length || 1) + " selected",
    });
  }, [state.queue.length]);

  const selectIndex = useCallback((resolve) => {
    const sessionId = sessionIdRef.current;
    dispatch({
      type: "select",
      sessionId,
      resolve,
      live: (next, s) => `Match ${next + 1} of ${s.queue.length} selected`,
    });
  }, []);

  // The index is resolved INSIDE the reducer, so two taps in one tick apply two
  // steps instead of both reading the same stale index.
  const step = useCallback(
    (delta) => {
      selectIndex((s) => (s.activeIndex + delta + s.queue.length) % s.queue.length);
    },
    [selectIndex]
  );

  const next = useCallback(() => step(1), [step]);
  const previous = useCallback(() => step(-1), [step]);

  const selectAt = useCallback(
    (index) => selectIndex((s) => Math.min(Math.max(0, index), s.queue.length - 1)),
    [selectIndex]
  );

  const setFrame = useCallback((frame) => {
    dispatch({ type: "frame", sessionId: sessionIdRef.current, frame });
  }, []);

  const openReveal = useCallback(() => {
    const sessionId = sessionIdRef.current;
    dispatch({ type: "openReveal", sessionId });
  }, []);

  const closeReveal = useCallback(() => {
    dispatch({ type: "closeReveal", sessionId: sessionIdRef.current });
  }, []);

  // Analytics for a selection change is reported as a rank, never an identity.
  const activeTrader = state.queue[state.activeIndex] || null;
  const prevRankRef = useRef(null);
  useEffect(() => {
    if (state.phase === "idle") {
      prevRankRef.current = null;
      return;
    }
    const rank = state.activeIndex + 1;
    if (prevRankRef.current !== null && prevRankRef.current !== rank) {
      cbRef.current.onTargetSelected?.({ rank });
    }
    prevRankRef.current = rank;
  }, [state.activeIndex, state.phase]);

  const derived = useMemo(() => {
    const animatedIds = animatedTargetIds(state.queue, maxAnimated);
    const isLive = state.phase !== "idle";
    return {
      animatedIds,
      isLive,
      isTracing: state.phase === "tracing" || state.phase === "landing",
      isSettled:
        state.phase === "browsing" ||
        state.phase === "docking" ||
        state.phase === "openingReveal",
      mode: countMode(state.queue.length),
      activeTrader,
      activeId: activeTrader ? idStr(activeTrader.id) : null,
    };
  }, [state.queue, state.phase, activeTrader, maxAnimated]);

  return {
    state,
    ...derived,
    start,
    cancel,
    next,
    previous,
    selectAt,
    setFrame,
    openReveal,
    closeReveal,
    completeDock,
  };
}
